'use client';

import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import type { CartSettingsConfig } from '@/lib/cart-settings';
import type { AnnouncementBarMessage } from './site-header.shared';

/**
 * Runtime (PostHog-overlaid) header settings, streamed INTO the already
 * rendered static header as a state update instead of rendering a second
 * header over it.
 *
 * The header is part of the static shell, rendered once from cached CMS
 * values. Its runtime overrides (remote flag flips for accounts/reviews, cart
 * reward tiers) used to stream as a whole replacement `<SiteHeader>` inside a
 * Suspense boundary — identical markup, but React swaps the DOM when the
 * boundary resolves, so the logo and every mega-menu image were re-created on
 * every page load (a visible flash). Now `HeaderRuntimeReceiver` streams in
 * with `fallback={null}` and pushes the values into this context; the header's
 * client components read them and re-render in place. Same pattern as the
 * PDP's `LiveAvailabilityReceiver`.
 */
export interface HeaderRuntimeOverrides {
	enableCustomerAccounts: boolean;
	enableReviews: boolean;
	cartSettings: CartSettingsConfig;
	announcementMessages: AnnouncementBarMessage[];
}

interface HeaderRuntimeContextValue {
	overrides: HeaderRuntimeOverrides | null;
	setOverrides: (overrides: HeaderRuntimeOverrides) => void;
}

const HeaderRuntimeContext = createContext<HeaderRuntimeContextValue | null>(null);

export function HeaderRuntimeProvider({ children }: { children: ReactNode }) {
	const [overrides, setOverrides] = useState<HeaderRuntimeOverrides | null>(null);
	const value = useMemo(() => ({ overrides, setOverrides }), [overrides]);

	return <HeaderRuntimeContext value={value}>{children}</HeaderRuntimeContext>;
}

/** Rendered by the streamed runtime island; publishes the runtime values. */
export function HeaderRuntimeReceiver({ overrides }: { overrides: HeaderRuntimeOverrides }) {
	const context = useContext(HeaderRuntimeContext);
	const setOverrides = context?.setOverrides;

	useEffect(() => {
		setOverrides?.(overrides);
	}, [overrides, setOverrides]);

	return null;
}

/** Runtime header values once they have streamed in; `null` until then (or outside the provider). */
export function useHeaderRuntime() {
	return useContext(HeaderRuntimeContext)?.overrides ?? null;
}
